import DeleteIcon from '@mui/icons-material/Delete';
import { Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';

import { FormInput } from '../../../components/hook-form/form-input.component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Icons } from '../../../assets/icons';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
import { getAddressAbbreviationError } from '../../../utils/abbreviations.util';
import { ProjectType } from '../../../models/project';

const lotTypeOption = [
  {
    value: 'TH',
    label: 'Townhome (TH)',
  },
  {
    value: 'SFR',
    label: 'Single-Family Residential (SFR)',
  },
];

const lotTypeCMOption = [
  {
    value: 'CM',
    label: 'Commercial (CM)',
  },
];

interface BuildingOtherFormProps {
  name: string;
  index?: number;
  onRemove?: (index: number) => void;
  projectID?: string;
  data?: any;
  syncWithDux: any;
  projectTypeValue?: any;
  existingData?: any;
  projectStreet?: string;
  projectCommunityType?: string;
}

export const BuildingOtherForm = ({
  name,
  index,
  onRemove,
  projectID,
  data,
  syncWithDux,
  projectTypeValue,
  existingData,
  projectStreet,
  projectCommunityType,
}: BuildingOtherFormProps) => {
  const { getValues, setValue, setError, clearErrors } = useFormContext();

  // const { data, isLoading } = useLoadData<ApiResponse<any>>(projectsApiKeyStore.generateLotName(projectID));

  useWatch({
    name: `${name}.jobsite` || [],
  });

  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  useEffect(() => {
    setValue(`${name}.lotType`, '');
  }, []);

  useEffect(() => {
    if (data && data?.data) {
      setValue(`${name}.jobsite`, data?.data?.unitName);
    }
  }, [data]);

  console.log('projectStreet', projectStreet);

  useEffect(() => {
    if (projectTypeValue && projectTypeValue !== 'RES') {
      setValue(`${name}.lotType`, 'CM');
      if (projectCommunityType === ProjectType.Buildings && !getValues(`${name}.address`)) {
        setValue(`${name}.address`, projectStreet);
      }
    }
  }, [projectTypeValue]);

  const jobsiteRef = useRef<string | undefined>(getValues(`${name}.jobsite`));

  const handleUnitNameChange = (value: string) => {
    const initialJobNumber = jobsiteRef.current || getValues(`${name}.jobsite`);

    const newJobNumber = `${initialJobNumber}${value}`.trim();
    setValue(`${name}.jobsite`, newJobNumber);

    const allUnits = existingData?.map((u: any) => normalize(u?.unitName));

    const isDuplicate = allUnits?.includes(normalize(value));

    if (isDuplicate) {
      setError(`${name}.unitName`, {
        type: 'manual',
        message: 'Lot/Unit name already exists.',
      });
    } else {
      clearErrors(`${name}.unitName`);
    }
  };

  const handleJobnameNameChange = (e: any) => {
    jobsiteRef.current = e;

    const allUnits = existingData?.map((u: any) => normalize(u?.jobsite));

    const isDuplicate = allUnits?.includes(normalize(e));

    if (isDuplicate) {
      setError(`${name}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      clearErrors(`${name}.jobsite`);
    }
  };

  const handleJobNumberChange = (value: string) => {
    const allUnits = existingData?.map((u: any) => normalize(u?.jobNumber));

    const isDuplicate = allUnits?.includes(normalize(value));

    if (isDuplicate) {
      setError(`${name}.jobNumber`, {
        type: 'manual',
        message: 'Lot/Unit name already exists.',
      });
    } else {
      clearErrors(`${name}.jobNumber`);
    }
  };

  const handleAddressChange = (value: string) => {
    const allUnits = existingData?.map((u: any) => normalize(u?.address));

    const abbrError = getAddressAbbreviationError(value);
    const isDuplicate = allUnits?.includes(normalize(value));

    if (abbrError) {
      setError(`${name}.address`, {
        type: 'manual',
        message: abbrError,
      });
    } else if (isDuplicate) {
      setError(`${name}.address`, {
        type: 'manual',
        message: 'Address already exists.',
      });
    } else {
      clearErrors(`${name}.address`);
    }
  };

  // const isProjectTypeRES = projectTypeValue === "RES";
  const isProjectTypeRES = true;

  return (
    <Card elevation={0} style={{ margin: '0' }}>
      <CardContent style={{ padding: '0', margin: '0' }}>
        <Grid container spacing={2}>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>Jobsite</b>}
            <FormInput
              // disabled={true}
              isShrink={true}
              name={`${name}.jobsite`}
              label="Jobsite"
              onChange={(e: any) => handleJobnameNameChange(e)}
              validationQueryRoute={(value: string) =>
                projectsApiKeyStore.checkDuplicateLotAddressJobsite(projectID, '', 0, '', value)
              }
            />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>OTHER</b>}
            <FormInput name={`${name}.commonAreaName`} label="Other" onChange={(e: any) => handleUnitNameChange(e)} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>FLOOR</b>}
            <FormInput name={`${name}.commonAreaFloorNo`} label="Floor No." type="number" />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>Builder Job Number</b>}
            <FormInput
              name={`${name}.jobNumber`}
              label="Builder Job Number"
              validationQueryRoute={(value: string) =>
                projectsApiKeyStore.checkDuplicateLotAddress(projectID, '', 0, '', '', value)
              }
              onChange={(e: any) => handleJobNumberChange(e)}
            />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block', minWidth: '250px' }}>ADDRESS</b>}
            <FormInput
              name={`${name}.address`}
              label="Address"
              validationQueryRoute={(value: string) => projectsApiKeyStore.checkDuplicateLotAddress(projectID, value)}
              onChange={(e: any) => handleAddressChange(e)}
            />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block', minWidth: '200px' }}>Square Feet</b>}
            <FormInput name={`${name}.squareFeet`} label={'Square Feet'} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block', minWidth: '200px' }}>Proctor Result</b>}

            <span style={{ marginBottom: '10px', display: 'block', minWidth: '200px' }}>
              <FormAutoCompleteSelectDynamic
                name={`${name}.proctorResult`}
                label="Proctor Result"
                getQueryKeyConfig={() => proctorResultApiKeyStore.getProjectProctorResults(Number(projectID))}
                optionsValueKey="proctorResultsID"
                optionsLabelKey="proctorFileName"
              />
            </span>
          </Grid>
          {isProjectTypeRES && (
            <Grid item lg={2} sm={2} xl={2} xs={2}>
              {index === 0 && <b style={{ marginBottom: '10px', display: 'block', minWidth: '200px' }}>Lot Type</b>}
              <FormSelect
                name={`${name}.lotType`}
                label="Lot Type"
                options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                error={true}
              />
            </Grid>
          )}
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && (
              <b style={{ marginBottom: '10px', display: 'block', minWidth: '250px' }}>BUILDING PERMIT</b>
            )}
            <FormInput label="Building Permit#" name={`${name}.buildingPermit`} />
          </Grid>
          {/* <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block', minWidth: '250px' }}>CREATE WO</b>}
            <FormCheckbox label="Create WO" name={`${name}.createWO`} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            {index === 0 && <b style={{ marginBottom: '10px', display: 'block', minWidth: '250px' }}>SYNC WITH DUX</b>}
            <FormCheckbox label="Sync With DUX" name={`${name}.syncWithDUX`} disabled={!syncWithDux} />
          </Grid> */}
          {onRemove && index !== undefined && index >= 0 && (
            <Grid item lg={2} sm={2} xl={2} xs={2}>
              <div style={{ display: 'flex', height: 'auto', paddingBottom: '19px', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', minHeight: '40px' }}>
                  <IconButton aria-label="delete" onClick={() => onRemove(index)}>
                    <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                      <img src={Icons.Delete} alt="DELETE" />
                    </Tooltip>
                  </IconButton>
                </div>
              </div>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
